import React, { useState } from "react";
import { Box, Typography, Tabs, Tab, CircularProgress } from '@mui/material';
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { WorkerStatusCard } from "../assets/style";
import { useGetOngoingPermitsQuery, useGetExpiredPermitsQuery, PermitStatusData } from "../features/permit/permitStatusSlice";

function PermitStatusCard() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [tab, setTab] = useState(0);

    // 使用不同的 API 查詢
    const { data: ongoingData = [], isLoading: ongoingLoading, error: ongoingError } = useGetOngoingPermitsQuery();
    const { data: expiredData = [], isLoading: expiredLoading, error: expiredError } = useGetExpiredPermitsQuery();

    // 進行中許可證的表格列定義
    const ongoingColumns: GridColDef[] = [
        // { field: 'workScheduleId', headerName: t('Work Schedule ID'), flex: 1, minWidth: 150 },
        { field: 'projectName', headerName: t('Project Name'), flex: 1, minWidth: 150 },
        { field: 'workDate', headerName: t('Work Date'), flex: 1, minWidth: 120 },
        { field: 'location', headerName: t('Location'), flex: 1, minWidth: 120 },
        {
            field: 'status',
            headerName: t('Status'),
            flex: 1,
            minWidth: 100,
            renderCell: (params) => (
                <Box
                    sx={{
                        backgroundColor: '#4caf50',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                    }}
                >
                    {t(params.value)}
                </Box>
            )
        },
    ];

    // 已過期許可證的表格列定義
    const expiredColumns: GridColDef[] = [
        // { field: 'workScheduleId', headerName: t('Work Schedule ID'), flex: 1, minWidth: 150 },
        { field: 'projectName', headerName: t('Project Name'), flex: 1, minWidth: 150 },
        { field: 'workDate', headerName: t('Work Date'), flex: 1, minWidth: 120 },
        { field: 'location', headerName: t('Location'), flex: 1, minWidth: 120 },
        {
            field: 'status',
            headerName: t('Status'),
            flex: 1,
            minWidth: 100,
            renderCell: (params) => (
                <Box
                    sx={{
                        backgroundColor: '#f44336',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.875rem',
                    }}
                >
                    {t(params.value)}
                </Box>
            )
        },
    ];

    // DataGrid requires id field
    const withId = (rows: PermitStatusData[]) => rows.map((row, idx) => ({ ...row, id: row.workScheduleId ?? idx }));

    // 處理行點擊事件
    const handleRowClick = (params: any) => {
        const rowData = params.row;
        navigate('/entry-detail', {
            state: {
                entry: {
                    id: rowData.workScheduleId,
                    projectName: rowData.projectName,
                    location: rowData.location,
                    workDate: rowData.workDate,
                    status: rowData.status
                }
            }
        });
    };

    // 處理載入狀態
    const isLoading = (tab === 0 && ongoingLoading) || (tab === 1 && expiredLoading);
    const hasError = (tab === 0 && ongoingError) || (tab === 1 && expiredError);

    return (
        <WorkerStatusCard sx={{ minHeight: '300px', maxHeight: '500px', "@media (max-width: 800px)": { maxHeight: '100%' } }}>
            <Box sx={{ height: '', marginTop: '10px', marginLeft: '15px', display: 'flex', flexDirection: 'column', "@media (max-width: 800px)": { marginLeft: 0 } }}>
                <Box>
                    <Typography variant="h6" gutterBottom>{t('Permit Status')}</Typography>
                    <Tabs value={tab} onChange={(_, v) => setTab(v)}>
                        <Tab label={t('Ongoing')} />
                        <Tab label={t('Expired')} />
                    </Tabs>
                </Box>
                <Box mt={2} sx={{ flex: 1, minHeight: 200 }}>
                    {isLoading && (
                        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                            <CircularProgress />
                        </Box>
                    )}

                    {hasError && (
                        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                            <Typography color="error">
                                {t('Error loading data. Please try again.')}
                            </Typography>
                        </Box>
                    )}

                    {!isLoading && !hasError && tab === 0 && (
                        <DataGrid
                            autoHeight
                            rows={withId(ongoingData)}
                            columns={ongoingColumns}
                            pagination
                            initialState={{
                                pagination: { paginationModel: { pageSize: 5, page: 0 } },
                            }}
                            onRowClick={handleRowClick}
                            disableColumnMenu
                            disableColumnSelector
                            disableDensitySelector
                            sx={{
                                '& .MuiDataGrid-cell': {
                                    borderBottom: '1px solid #e0e0e0',
                                    cursor: 'pointer',
                                },
                                '& .MuiDataGrid-row:hover': {
                                    backgroundColor: '#f5f5f5',
                                },
                                '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: '#f5f5f5',
                                    borderBottom: '2px solid #e0e0e0',
                                },
                                '& .MuiDataGrid-footerContainer .MuiTablePagination-selectLabel, .MuiDataGrid-footerContainer .MuiTablePagination-select': {
                                    display: 'none',
                                },
                            }}
                        />
                    )}

                    {!isLoading && !hasError && tab === 1 && (
                        <DataGrid
                            autoHeight
                            rows={withId(expiredData)}
                            columns={expiredColumns}
                            pagination
                            initialState={{
                                pagination: { paginationModel: { pageSize: 5, page: 0 } },
                            }}
                            onRowClick={handleRowClick}
                            disableColumnMenu
                            disableColumnSelector
                            disableDensitySelector
                            sx={{
                                '& .MuiDataGrid-cell': {
                                    borderBottom: '1px solid #e0e0e0',
                                    cursor: 'pointer',
                                },
                                '& .MuiDataGrid-row:hover': {
                                    backgroundColor: '#f5f5f5',
                                },
                                '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: '#f5f5f5',
                                    borderBottom: '2px solid #e0e0e0',
                                },
                                '& .MuiDataGrid-footerContainer .MuiTablePagination-selectLabel, .MuiDataGrid-footerContainer .MuiTablePagination-select': {
                                    display: 'none',
                                },
                            }}
                        />
                    )}
                </Box>
            </Box>
        </WorkerStatusCard>
    );
}

export default PermitStatusCard;